/** India Standard Time — Asia/Kolkata (UTC+5:30). */

export const INDIA_TIME_ZONE = "Asia/Kolkata";
export const INDIA_LOCALE = "en-IN";

export function parseIso(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatIndiaDate(iso, options = {}) {
  const d = parseIso(iso);
  if (!d) return null;
  return d.toLocaleDateString(INDIA_LOCALE, {
    timeZone: INDIA_TIME_ZONE,
    ...options,
  });
}

export function formatIndiaDateTime(iso, options = {}) {
  const d = parseIso(iso);
  if (!d) return null;
  return d.toLocaleString(INDIA_LOCALE, {
    timeZone: INDIA_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  });
}

/** YYYY-MM-DDTHH:mm for datetime-local inputs in IST. */
export function nowIndiaDatetimeLocal() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: INDIA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  const hour = parts.find((p) => p.type === "hour")?.value;
  const minute = parts.find((p) => p.type === "minute")?.value;
  if (!y || !m || !day || !hour || !minute) return "";
  return `${y}-${m}-${day}T${hour}:${minute}`;
}

/** Convert datetime-local value (IST wall clock) to ISO-8601 with +05:30 offset. */
export function indiaDatetimeLocalToIso(localValue) {
  const v = String(localValue || "").trim();
  if (!v) return null;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return null;
  return `${v}:00+05:30`;
}

/** Current instant as ISO-8601 with India (+05:30) offset, including seconds. */
export function nowIndiaIso() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: INDIA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  const hour = parts.find((p) => p.type === "hour")?.value;
  const minute = parts.find((p) => p.type === "minute")?.value;
  const second = parts.find((p) => p.type === "second")?.value;
  if (!y || !m || !day || !hour || !minute || !second) return new Date().toISOString();
  return `${y}-${m}-${day}T${hour}:${minute}:${second}+05:30`;
}

/** YYYY-MM-DD for the current calendar day in IST. */
export function todayIndiaIsoDate() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: INDIA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  return y && m && day ? `${y}-${m}-${day}` : null;
}

/** True when both instants fall on the same calendar day in IST. */
export function isSameIndiaCalendarDay(isoA, isoB = new Date().toISOString()) {
  const fmt = (iso) => {
    const d = parseIso(iso);
    if (!d) return "";
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: INDIA_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  };
  return fmt(isoA) === fmt(isoB);
}
